import { computed, inject } from '@angular/core';
import { Config } from '../../features/chat/interfaces/config.interface';
import { ConfigService } from '../services/config.service';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { AuthStore } from '../../features/auth/store/auth.store';
import { AdminService } from '../../features/admin/services/admin.service';
import { NotificationService } from '../ui/notification/notification.service';

type ConfigState = {
    clientId: string;
    config: Config | null;
    context: string;
    previewMode: boolean;
};

const initialState: ConfigState = {
    clientId: null,
    config: null,
    context: null,
    previewMode: false,
};

export const ConfigStore = signalStore(
    { providedIn: 'root' },
    withState(initialState),
    withComputed((store) => ({
        hasConfig: computed(() => store.config() !== null),
        hasClientId: computed(() => store.clientId() !== null),
        chatbotName: computed(() => store.config().chatbotName),
        isPreviewMode: computed(() => store.previewMode()),
    })),
    withMethods(
        (
            store,
            configService = inject(ConfigService),
            adminService = inject(AdminService),
            authStore = inject(AuthStore),
            notificationService = inject(NotificationService),
        ) => ({
            loadAdminConfig(): void {
                configService.getAdminConfig().subscribe({
                    next: (config) => {
                        patchState(store, { config });
                    },
                    error: (error) => {
                        // If config not found, create default config
                        if (error.status === 404) {
                            this.createDefaultConfig();
                        }
                    },
                });
            },

            loadClientConfig(): void {
                if (store.hasClientId()) {
                    configService.getClientConfig(store.clientId()).subscribe({
                        next: (config) => {
                            patchState(store, { config });
                        },
                        error: (error) => {
                            console.error('Error loading client config:', error);
                        },
                    });
                }
            },

            createDefaultConfig(): void {
                const defaultConfig: Config = {
                    chatbotName: 'Chatbot',
                    enableReceiveMessageSound: true,
                    enableSendMessageSound: true,
                    primaryColor: '#000000',
                    showTrademark: true,
                    chatbotImage: 'https://cdn.prod.website-files.com/6542a25562a6c6205a78b7d4/6760554a511ca551fe2e03d6_alto.jpg',
                    welcomeMessage: "Bonjour je suis Alto, l'assistant virtuel de Nubios, comment puis-je vous aider ?",
                    nlpTopN: 3,
                    nlpThreshold: 0.05,
                    temperature: 0.7,
                    maxOutputTokens: 400,
                    topK: 100,
                    topP: 0.9,
                    suggestions: [],
                    platform: 'GOOGLE',
                    waitingMessage: 'Un petit instant, je me connecte à mes neurones...',
                    blacklistUrls: [],
                };

                patchState(store, { config: defaultConfig });

                configService.updateConfig(defaultConfig).subscribe({
                    next: (updatedConfig) => {
                        patchState(store, { config: updatedConfig });
                    },
                    error: (error) => {
                        console.error('Error creating default config:', error);
                    },
                });
            },

            initClientId(): void {
                const clientId = adminService.getClientIdBasedOnToken(authStore.token());
                this.setClientId(clientId);
            },

            setClientId(clientId: string): void {
                patchState(store, { clientId });
            },

            setPreviewMode(preview: boolean): void {
                patchState(store, { previewMode: preview });
            },

            // apply new config to preview it without saving it in backend
            applyNewConfig(newConfig: Config): void {
                patchState(store, { config: newConfig });
            },

            saveConfig(config: Config): void {
                configService.updateConfig(config).subscribe({
                    next: () => {
                        patchState(store, { config });
                        notificationService.addNotification({
                            title: 'Configuration sauvegardée',
                            message: 'La configuration a été sauvegardée avec succès.',
                            type: 'success',
                        });
                    },
                    error: (error) => {
                        console.error('Error saving config:', error);
                    },
                });
            },

            saveBlacklistUrls(urls: string[]): void {
                const config: Config = {
                    ...store.config(),
                    blacklistUrls: urls,
                };
                configService.updateConfig(config).subscribe({
                    next: () => {
                        patchState(store, { config });
                        notificationService.addNotification({
                            title: 'Configuration sauvegardée',
                            message: 'La configuration a été sauvegardée avec succès.',
                            type: 'success',
                        });
                    },
                    error: (error) => {
                        console.error('Error saving config:', error);
                    },
                });
            },

            saveContext(context: string): void {
                configService.updateContext(context).subscribe({
                    next: (res) => {
                        patchState(store, { context: res.persistentContext });
                        notificationService.addNotification({
                            title: 'Contexte sauvegardé',
                            message: 'Le contexte a été sauvegardé avec succès.',
                            type: 'success',
                        });
                    },
                    error: (error) => {
                        console.error('Error saving context:', error);
                    },
                });
            },

            getContext(): void {
                configService.getContext().subscribe({
                    next: (res) => {
                        patchState(store, { context: res.persistentContext });
                    },
                    error: (error) => {
                        console.error('Error getting context:', error);
                    },
                });
            },

            toggleNotificationSound(): void {
                patchState(store, {
                    config: {
                        ...store.config(),
                        enableReceiveMessageSound: !store.config().enableReceiveMessageSound,
                        enableSendMessageSound: !store.config().enableSendMessageSound,
                    },
                });
            },

            reset(): void {
                patchState(store, initialState);
            },
        }),
    ),
);
